/**
 * Session state for the SPA. Token stays httpOnly — never read in JS (plan §6.8).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type JSX,
  type ReactNode,
  type SetStateAction,
} from 'react';

import { AuthSessionResponseSchema } from '@meridian/contracts';

import type { AppConfig } from '@/config';

import { isLocalAuthDisabled } from './local-bypass';
import { readOptionalJson } from './read-optional-json';

/**
 * `'unavailable'` is a TERMINAL "we could not tell", distinct from `'loading'`.
 *
 * Every other member answers the question; this one records that the probe
 * failed with no prior authenticated status to fall back on — a rejected fetch
 * or a non-401 error response on FIRST boot. It exists because the alternatives
 * are both wrong: `'unauthenticated'` would redirect to `/login`, discarding the
 * deep link and telling the user they are signed out when the truth is that
 * nobody answered (and the login page would not reach the server either), while
 * `'loading'` — what this used to fall back to — renders the guard's spinner
 * forever, with no redirect, no retry and no error.
 *
 * Widening this union is safe today because NO consumer infers "signed in" from
 * anything but the literal `'authenticated'`. The three sites, stated as they
 * actually read — the form varies, and only the comparand matters:
 *
 *   - `route-guard.tsx:58`  `=== 'authenticated'` — grants access.
 *   - `router-root.tsx:110` `!== 'authenticated'` — withholds the account slot.
 *   - `account-menu.tsx:74` `!== 'authenticated'` — renders nothing.
 *
 * Both forms fail closed for a new member, so either is fine. The rule to keep
 * is about the COMPARAND, not the operator: never decide access by excluding a
 * non-authenticated member (`!== 'unauthenticated'`, `!== 'loading'`), because
 * that grants every future member access by default.
 *
 * `route-guard.tsx:38` does use `!== 'unauthenticated'`, and is not an
 * exception: it gates the login REDIRECT, not access. Reading it as a template
 * for an access check is the mistake this paragraph exists to prevent — which
 * is precisely why 'unavailable' correctly does not redirect.
 *
 * That rule is enforced by the compiler, not by review. `route-guard.tsx`
 * switches over this union exhaustively with no `default`, so adding a member
 * here without handling it there fails the build ("Function lacks ending return
 * statement"). Widening is therefore safe to attempt: the type system will tell
 * you where to go. It will NOT tell you if you then handle the new member as
 * authenticated — that part is still on you, and the paragraph above is why.
 */
export type AuthStatus =
  'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'unavailable' | 'bypassed';

export interface AuthUser {
  readonly handle: string;
}

/**
 * What a session probe actually learned — three outcomes, not two.
 *
 * A boolean collapsed the two NEGATIVE cases into one, and they are not the
 * same fact. `refreshSession` answers "no" both when the server said the
 * session is gone (401) and when it could not be asked at all (rejected fetch,
 * or a non-OK non-401 response) — and in the latter two it deliberately
 * RETAINS 'authenticated'. A caller reading `false` could not tell "you are
 * signed out" from "we could not check", so `add-passkey.tsx` told a user with
 * a perfectly good session that it had expired whenever a transient blip
 * happened to land on the probe.
 *
 * 'unevaluable' is therefore a first-class answer: it means the status was
 * left as it was, and the caller must fall back to its generic handling rather
 * than assert anything about the session.
 */
export type SessionProbe = 'authenticated' | 'unauthenticated' | 'unevaluable';

export interface AuthContextValue {
  readonly status: AuthStatus;
  readonly user: AuthUser | null;
  readonly logout: () => Promise<void>;
  readonly refresh: () => Promise<SessionProbe>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthEndpoint {
  readonly url: string;
  readonly request: RequestInit;
}

function requestInit(mode: RequestCredentials): RequestInit {
  return { credentials: mode };
}

export function authEndpoint(config: AppConfig, path: string): AuthEndpoint {
  const base = config.apiBaseUrl.replace(/\/$/, '');
  if (!base.startsWith('http://') && !base.startsWith('https://')) {
    return { url: `/auth/${path}`, request: requestInit('same-origin') };
  }
  return { url: `${new URL(base).origin}/auth/${path}`, request: requestInit('include') };
}

export interface AuthProviderProps {
  readonly config: AppConfig;
  readonly children: ReactNode;
}

type StatusSetter = Dispatch<SetStateAction<AuthStatus>>;
type UserSetter = Dispatch<SetStateAction<AuthUser | null>>;

async function refreshSession(
  config: AppConfig,
  setStatus: StatusSetter,
  setUser: UserSetter,
  isCurrent: () => boolean,
): Promise<SessionProbe> {
  /* Bypass and superseded probes are 'unevaluable', not 'unauthenticated':
     neither learned anything about a session, and a caller must not report
     expiry off either. */
  if (isLocalAuthDisabled(config)) {
    setStatus('bypassed');
    setUser(null);
    return 'unevaluable';
  }
  /* No generation check here: `refresh` increments the generation and calls
     this synchronously, and nothing above awaits, so `isCurrent()` cannot yet
     be false. Stated rather than guarded — a branch that can never be taken is
     permanently unexecuted coverage (style §3). The checks below are the real
     ones; each follows an actual suspension point. Add one back the moment an
     `await` appears above this line. */
  setStatus((previous) => (previous === 'authenticated' ? previous : 'loading'));
  try {
    const endpoint = authEndpoint(config, 'session');
    const res = await fetch(endpoint.url, {
      method: 'GET',
      ...endpoint.request,
      headers: { Accept: 'application/json' },
    });
    if (!isCurrent()) return 'unevaluable';
    return await applySessionResponse(res, setStatus, setUser, isCurrent);
  } catch {
    if (!isCurrent()) return 'unevaluable';
    /* The server never answered — status is RETAINED, so the probe reports
       that it could not evaluate rather than that the session is gone. */
    retainAuthenticatedStatus(setStatus);
    return 'unevaluable';
  }
}

async function applySessionResponse(
  res: Response,
  setStatus: StatusSetter,
  setUser: UserSetter,
  isCurrent: () => boolean,
): Promise<SessionProbe> {
  /* Entered only from `refreshSession`'s single call site, on the statement
     immediately after its own post-fetch generation check, so a re-check here
     could never differ. The check after `readOptionalJson` below is the one
     that can: it follows an await. */
  if (res.status === 401) {
    setUnauthenticated(setStatus, setUser);
    return 'unauthenticated';
  }
  if (!res.ok) {
    /* Ambiguous: the status is retained, so the probe learned nothing. */
    retainAuthenticatedStatus(setStatus);
    return 'unevaluable';
  }
  const body = await readOptionalJson(res);
  if (!isCurrent()) return 'unevaluable';
  const user = authenticatedUser(body);
  if (user !== null) {
    setStatus('authenticated');
    setUser(user);
    return 'authenticated';
  }
  /* A 2xx that proves nothing DOES answer the question — fail closed. */
  setUnauthenticated(setStatus, setUser);
  return 'unauthenticated';
}

/**
 * The session response either proves a session or it does not — anything that
 * is not the confirmed shape reads as unauthenticated, so this guard fails
 * closed (security.md §2).
 *
 * Scope note: "fails closed" describes THIS path — a 2xx whose body does not
 * prove a session. It is not a property of the whole file. A non-OK, non-401
 * response (`applySessionResponse`'s `!res.ok` branch) and a rejected fetch
 * (`refreshSession`'s `catch`) both still RETAIN 'authenticated', deliberately:
 * a server that never answered is ambiguous, whereas a 200 carrying nothing is
 * not.
 *
 * This guard began as a replacement for an unchecked `as` cast over
 * `res.json()`. The cast asserted the shape instead of checking it, so a
 * body-less 200 threw SyntaxError out of `json()` into refreshSession's catch,
 * which retained 'authenticated' — the SPA kept rendering protected chrome off
 * a response that confirmed nothing, and a missing `handle` produced a user
 * whose handle was `undefined`. That defect is two generations old; the
 * paragraph below carries the live rationale.
 *
 * The narrowing itself now comes from `@meridian/contracts`, not from fields
 * spelled out here. The hand-written version was honest but it was a SECOND
 * definition of the wire shape, bound to the server's by nothing: renaming
 * `handle` in `apps/api` left both suites green and redirected every
 * authenticated user to `/login` on boot. One schema, parsed here and used to
 * type the route there, is what makes that a compile error instead.
 */
function authenticatedUser(body: unknown): AuthUser | null {
  const envelope = AuthSessionResponseSchema.safeParse(body);
  if (!envelope.success) return null;
  const parsed = envelope.data;
  if (!parsed.success) return null;
  return { handle: parsed.data.handle };
}

function setUnauthenticated(setStatus: StatusSetter, setUser: UserSetter): void {
  setStatus('unauthenticated');
  setUser(null);
}

/**
 * Retain an established session across an ambiguous probe; otherwise land in
 * the terminal `'unavailable'`.
 *
 * The retain arm is the important one and is unchanged: a transient 503 or a
 * dropped connection mid-session is not a reason to tear down the chrome, and
 * the server re-authorises every protected call regardless. Only the no-session
 * fallback moved — it used to be `'loading'`, which the guard renders as a
 * spinner that nothing ever clears.
 */
function retainAuthenticatedStatus(setStatus: StatusSetter): void {
  setStatus((previous) => (previous === 'authenticated' ? 'authenticated' : 'unavailable'));
}

export function AuthProvider({ config, children }: AuthProviderProps): JSX.Element {
  const bypassed = isLocalAuthDisabled(config);
  const [status, setStatus] = useState<AuthStatus>(bypassed ? 'bypassed' : 'loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const refreshGeneration = useRef(0);

  const refresh = useCallback(async () => {
    const generation = ++refreshGeneration.current;
    return refreshSession(
      config,
      setStatus,
      setUser,
      () => refreshGeneration.current === generation,
    );
  }, [config]);

  const logout = useCallback(async () => {
    refreshGeneration.current += 1;
    try {
      const endpoint = authEndpoint(config, 'logout');
      await fetch(endpoint.url, {
        method: 'POST',
        ...endpoint.request,
      });
    } catch {
      /* still clear local state */
    }
    setUser(null);
    setStatus('unauthenticated');
  }, [config]);

  useEffect(() => {
    if (bypassed) return;
    void refresh();
  }, [bypassed, refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, logout, refresh }),
    [status, user, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
